import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Check } from "lucide-react";
import { toast } from "sonner";

// US states TopoJSON (Albers USA projection built into react-simple-maps)
const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const CITIES = [
  { key: "New York", label: "New York", coords: [-74.006, 40.7128], active: true },
  { key: "Miami", label: "Miami", coords: [-80.1918, 25.7617], active: true },
  { key: "Los Angeles", label: "Los Angeles", coords: [-118.2437, 34.0522], active: true },
  { key: "San Francisco", label: "San Francisco", coords: [-122.4194, 37.7749], active: true },
];
export const ACTIVE_CITIES = CITIES;
export const UPCOMING_CITIES = ["Chicago", "Boston", "Washington DC", "Austin", "Seattle", "Atlanta", "Houston", "Dallas", "Other"];

function NotifyMe() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!city || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error(t("notify_err")); return; }
    setBusy(true);
    try { await api.post("/waitlist", { email, city }); setDone(true); toast.success(t("notify_ok")); }
    catch { toast.error(t("notify_err")); } finally { setBusy(false); }
  };
  return (
    <div data-testid="notify-me" className="mt-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6 sm:p-8 grid lg:grid-cols-2 gap-6 items-center">
      <div>
        <div className="font-serif text-2xl sm:text-3xl text-white">{t("notify_title")}</div>
        <p className="text-white/70 text-sm mt-2">{t("notify_sub")}</p>
      </div>
      {done ? (
        <div data-testid="notify-success" className="text-white flex items-center gap-2"><Check size={18} className="text-emerald-300" />{t("notify_ok")}</div>
      ) : (
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger data-testid="notify-city" className="h-12 bg-white border-0 text-stone-900 sm:w-44"><SelectValue placeholder={t("notify_city_ph")} /></SelectTrigger>
            <SelectContent className="bg-white">{UPCOMING_CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="notify-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("notify_email_ph")} className="h-12 bg-white border-0 text-stone-900 flex-1" />
          <Button data-testid="notify-submit" type="submit" disabled={busy} className="h-12 rounded-xl bg-white text-black hover:bg-stone-200 px-6 btn-pill">{busy ? "..." : t("notify_btn")}</Button>
        </form>
      )}
    </div>
  );
}

export default function USMap() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [hover, setHover] = useState(null);

  const go = (c) => {
    if (!c.active) return;
    nav(`/services?location=${encodeURIComponent(c.key)}`);
  };

  return (
    <section className="relative py-24 overflow-hidden" data-testid="us-map-section">
      {/* Brickell skyline backdrop */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?auto=format&fit=crop&w=1800&q=75"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/95 via-stone-900/85 to-stone-900/95" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-10">
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-white">{t("cities_title")}</h2>
          <p className="text-white/70 mt-3 text-sm">{t("cities_subtitle")}</p>
        </div>

      <div className="relative bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6 sm:p-10 overflow-hidden">
        {/* soft grid backdrop */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative">
          <ComposableMap
            projection="geoAlbersUsa"
            projectionConfig={{ scale: 1100 }}
            width={960}
            height={560}
            style={{ width: "100%", height: "auto" }}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="rgba(255,255,255,0.85)"
                    stroke="#78716c"
                    strokeWidth={0.6}
                    style={{
                      default: { outline: "none" },
                      hover: { fill: "#ffffff", outline: "none" },
                      pressed: { outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>

            {CITIES.map((c) => {
              const isHovered = hover === c.key;
              return (
                <Marker
                  key={c.key}
                  coordinates={c.coords}
                  onMouseEnter={() => setHover(c.key)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => go(c)}
                  data-testid={`city-pin-${c.key.toLowerCase().replace(/ /g, "-")}`}
                  style={{
                    default: { cursor: c.active ? "pointer" : "not-allowed" },
                    hover: { cursor: c.active ? "pointer" : "not-allowed" },
                    pressed: { cursor: c.active ? "pointer" : "not-allowed" },
                  }}
                >
                  {c.active && (
                    <circle r={18} fill="#1c1917" opacity={0.1}>
                      <animate attributeName="r" values="10;22;10" dur="2.4s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.2;0;0.2" dur="2.4s" repeatCount="indefinite" />
                    </circle>
                  )}
                  <circle
                    r={isHovered ? 8 : 6}
                    fill={c.active ? "#1c1917" : "#a8a29e"}
                    stroke="#fff"
                    strokeWidth={2}
                    style={{ transition: "r 0.2s ease" }}
                  />
                  <text
                    textAnchor="middle"
                    y={isHovered ? -16 : -12}
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: 14,
                      fontWeight: isHovered ? 700 : 600,
                      fill: "#ffffff",
                      pointerEvents: "none",
                      transition: "y 0.2s ease",
                      paintOrder: "stroke",
                      stroke: "rgba(28,25,23,0.7)",
                      strokeWidth: 3,
                      strokeLinejoin: "round",
                    }}
                  >
                    {c.label}
                  </text>
                  {!c.active && isHovered && (
                    <text
                      textAnchor="middle"
                      y={22}
                      style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: 10,
                        fill: "#78716c",
                        pointerEvents: "none",
                      }}
                    >
                      {t("coming_soon")}
                    </text>
                  )}
                </Marker>
              );
            })}
          </ComposableMap>
        </div>

        {/* City chips (mobile-friendly & keyboard accessible) */}
        <div className="relative mt-8 flex flex-wrap gap-2 justify-center">
          {CITIES.filter((c) => c.active).map((c) => (
            <button
              key={c.key}
              onClick={() => go(c)}
              data-testid={`city-chip-${c.key.toLowerCase().replace(/ /g, "-")}`}
              className="px-4 py-2 rounded-full bg-white text-stone-900 text-sm hover:bg-stone-100 transition-colors btn-pill"
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <NotifyMe />
      </div>
    </section>
  );
}
